import styled from '@emotion/styled';
import { Spacings, mediaQuery, Fonts } from '@styles/index';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import H6 from '@base/H6/H6';
import { useEffect, useRef } from 'react';
import useRequestStatusStore from '@stores/requestStatus';
import { getFetchingUpdater } from '@stores/selectors/requestStatus';
import ItemCard from './BatchScheduleItem';
import useCurriculumSection from '../useCurriculumSection';

const SectionWrapper = styled.div`
  margin-top: ${Spacings.SPACING_8B};
`;

export const ViewMoreMultipleSection = styled(H6)`
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-primary);
  grid-column: 9/25;
  background-color: var(--color-base-1);
  height: 18px;
  font-weight: ${Fonts.SEMIBOLD};
  font-size: ${Fonts.SMALL};
  line-height: ${Spacings.SPACING_18};
  cursor: pointer;
  margin-bottom: ${Spacings.SPACING_2B};
  ${mediaQuery.uptoMobile} {
    grid-column: span 10;
    margin-bottom: ${Spacings.SPACING_0B};
  }
`;
const ViewMoreCTA = styled(ViewMoreMultipleSection)`
  background-color: var(--color-base-1);
  height: 70px;
  text-align: center;
  padding: ${Spacings.SPACING_6B};
  border-radius: ${Spacings.SPACING_2B};
  box-shadow: ${boxShadowGenerator()};
  color: var(--color-text-primary);
  ${mediaQuery.uptoMobile} {
    height: 56px;
    padding: ${Spacings.SPACING_18} ${Spacings.SPACING_0B};
    font-size: ${Fonts.SMALL};
  }
`;

export const getHasNextUrl = (classCount = 0, fetchedData = []) => {
  return classCount > fetchedData?.length;
};

const ItemContainer = styled.div`
  padding: ${Spacings.SPACING_6B};
  background-color: var(--color-base-1);
  border-radius: ${Spacings.SPACING_2B};
  margin-bottom: ${Spacings.SPACING_6B};
  box-shadow: ${boxShadowGenerator()};
  display: flex;
  height: 80px;
  transition: box-shadow 0.2s ease-in-out;
  ${mediaQuery.uptoMobile} {
    width: 100%;
    padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_4B};
    margin-bottom: ${Spacings.SPACING_4B};
  }
`;

const DummyCards = ({ limit }) => {
  return (
    <>
      {Array(limit)
        .fill(1)
        .map((val, index) => {
          return <ItemContainer key={`dummy_${val + index}`} />;
        })}
    </>
  );
};

const CurriculumSection = ({
  goalUID,
  course,
  isMultipleCourses,
  batchGroupUID,
  classType,
  lastPrimarySourceSection,
  isFetchData,
  segmentData
}) => {
  const { classes = [], limit, url, classCount } = course;
  const curriculumSection = useCurriculumSection({
    lessonGroupId: course.lessonGroupId,
    url,
    limit,
    classType,
    isFetchData
  });
  const {
    setSize: setNumberOfPages,
    curriculumSectionData,
    isValidating
  } = curriculumSection;
  const { results = [] } = curriculumSectionData;
  const dataToRender = results?.length ? results : classes;
  const hasNextUrl = getHasNextUrl(classCount, dataToRender);
  const fetchingTimeOutRef = useRef(null);

  const clearLoadTimeout = () => {
    if (fetchingTimeOutRef.current)
      fetchingTimeOutRef.current = clearTimeout(fetchingTimeOutRef.current);
  };

  useEffect(() => {
    return () => {
      clearLoadTimeout();
    };
  }, []);

  useEffect(() => {
    if (fetchingTimeOutRef.current && !isFetchData) clearLoadTimeout();
    if (isFetchData && hasNextUrl) {
      fetchingTimeOutRef.current = setTimeout(() => {
        setNumberOfPages((nPages) => nPages);
      }, 700);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetchData]);

  const setFetching = useRequestStatusStore(getFetchingUpdater);

  useEffect(() => {
    if (isValidating) {
      setFetching(true);
    } else {
      setFetching(false);
    }
  }, [isValidating, setFetching]);

  const loadMoreCourses = () => {
    if (!isValidating && hasNextUrl) {
      setNumberOfPages((nPages) => nPages + 1);
    }
  };

  return (
    <SectionWrapper>
      {dataToRender.map((lesson, index) => {
        const lastElement = dataToRender.length === index + 1;
        const isLastElement = !hasNextUrl && lastElement;
        return (
          <ItemCard
            key={lesson.uid}
            item={lesson}
            goalUID={goalUID}
            isMultipleCourses={isMultipleCourses}
            batchGroupUID={batchGroupUID}
            lastPrimarySourceSection={lastPrimarySourceSection}
            segmentData={segmentData}
            isLastElement={isLastElement}
          />
        );
      })}
      {classCount > 0 && !dataToRender?.length && <DummyCards limit={limit} />}
      {hasNextUrl && (
        <>
          {isMultipleCourses ? (
            <ViewMoreMultipleSection onClick={loadMoreCourses}>
              View more
            </ViewMoreMultipleSection>
          ) : (
            <ViewMoreCTA onClick={loadMoreCourses}>View more</ViewMoreCTA>
          )}
        </>
      )}
    </SectionWrapper>
  );
};
export default CurriculumSection;
